import type { TFunction } from 'i18next';
import { ExternalLink, Key } from 'lucide-react';
import type { HeroConfig } from '../types';
import { ACCENT, PILL_HOVER, BORDER_HOVER_30, OUTLINE } from '../tokens';

interface Props {
  hero: HeroConfig;
  t: TFunction;
}

export function PortfolioHero({ hero, t }: Props) {
  const a = ACCENT[hero.accent];
  const badgeGlowGradient = hero.badgeGlowGradient ?? 'from-neon-purple/20 to-neon-cyan/20';
  const pillAccent = hero.pillAccent ?? hero.accent;

  return (
    <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
      {/* Badge */}
      {hero.badgeGlow ? (
        <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6 relative group overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-r ${badgeGlowGradient} blur group-hover:blur-md transition-all duration-500`} />
          <span className={`w-2 h-2 ${a.dot} rounded-full animate-pulse relative z-10`} />
          <span className={`text-sm font-medium ${a.text} tracking-wide relative z-10`}>{t('badge')}</span>
        </div>
      ) : (
        <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-6">
          <span className={`w-2 h-2 ${a.dot} rounded-full animate-pulse`} />
          <span className={`text-sm font-medium ${a.text} tracking-wide`}>{t('badge')}</span>
        </div>
      )}

      {/* Title */}
      <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-8 font-heading">
        <span className="text-text-primary block mb-2">{t('title1')}</span>
        {hero.titleGradient === 'gradient-text' ? (
          <span className="gradient-text">{t('title2')}</span>
        ) : (
          <span className={`${hero.titleNowrap ? 'whitespace-nowrap ' : ''}bg-gradient-to-r ${hero.titleGradient} bg-clip-text text-transparent`}>
            {t('title2')}
          </span>
        )}
      </h1>

      {/* Description */}
      <p className="text-lg md:text-xl text-text-secondary leading-relaxed mb-10 max-w-2xl mx-auto">
        {t('description')}
      </p>

      {/* Tech pills */}
      <div className={`flex flex-wrap items-center justify-center gap-2 sm:gap-4 ${hero.cta?.variant === 'credentials' ? 'mb-12' : 'mb-8'}`}>
        {hero.tech.map((tech) => (
          <span
            key={tech}
            className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full glass-card text-text-secondary border border-white/5 ${PILL_HOVER[pillAccent]} transition-colors cursor-default`}
          >
            {tech}
          </span>
        ))}
      </div>

      {/* CTA */}
      {hero.cta && <HeroCtaView hero={hero} t={t} />}
    </div>
  );
}

function HeroCtaView({ hero, t }: { hero: HeroConfig; t: TFunction }) {
  const cta = hero.cta!;

  if (cta.variant === 'outline') {
    const o = OUTLINE[hero.accent];
    return (
      <div className="flex justify-center mt-10">
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`group relative inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/5 hover:bg-white/10 border ${o.border} transition-all duration-300 overflow-hidden transform hover:-translate-y-1 ${o.shadow}`}
        >
          <div className={`absolute inset-0 bg-gradient-to-r ${o.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
          <span className="relative z-10 text-lg font-bold text-white group-hover:text-neon-cyan transition-colors">{t(cta.buttonKey)}</span>
          <ExternalLink className="relative z-10 w-5 h-5 text-white group-hover:text-neon-cyan transition-colors" />
        </a>
      </div>
    );
  }

  if (cta.variant === 'gradientPulse') {
    return (
      <div className="relative inline-flex flex-col items-center gap-3">
        <div className="absolute -inset-3 bg-gradient-to-r from-neon-purple to-neon-cyan rounded-full blur-xl opacity-40 animate-pulse" />
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          className="relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-neon-purple to-neon-cyan text-white font-bold text-base sm:text-lg hover:scale-105 active:scale-95 transition-transform shadow-2xl shadow-neon-purple/40 border border-white/20"
        >
          <span className="w-2.5 h-2.5 bg-white rounded-full animate-ping absolute left-5 opacity-75" />
          <span className="w-2.5 h-2.5 bg-white rounded-full relative left-0" />
          {t(cta.buttonKey)}
          <ExternalLink className="w-5 h-5" />
        </a>
        {cta.noteKey && <p className="text-xs text-text-muted tracking-wide">{t(cta.noteKey)}</p>}
      </div>
    );
  }

  // credentials
  return (
    <div className="flex flex-col items-center justify-center gap-6 mt-8">
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-8 py-4 bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30 rounded-full hover:bg-neon-cyan/20 hover:scale-105 transition-all font-semibold shadow-[0_0_20px_rgba(0,255,255,0.2)]"
      >
        {t(cta.buttonKey)}
        <ExternalLink className="w-5 h-5" />
      </a>

      <div className="glass-card rounded-2xl p-6 border border-white/10 max-w-2xl w-full text-left flex flex-col gap-5">
        <div className="flex items-center gap-2 text-text-primary font-heading font-semibold text-xl border-b border-white/5 pb-3">
          <Key className="w-5 h-5 text-neon-purple" />
          {t(cta.credentialsTitleKey)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {cta.roles.map((role) => {
            const ra = ACCENT[role.accent];
            return (
              <div key={role.email} className={`bg-dark/50 p-5 rounded-xl border border-white/5 ${BORDER_HOVER_30[role.accent]} transition-colors group`}>
                <div className={`text-sm ${ra.text} font-bold mb-4 uppercase tracking-wider`}>{t(role.labelKey)}</div>
                <div className="space-y-3">
                  <div className="text-sm text-text-secondary flex justify-between items-center">
                    <span>Email:</span>
                    <span className="text-text-primary font-mono bg-dark-200 px-2 py-1 rounded">{role.email}</span>
                  </div>
                  <div className="text-sm text-text-secondary flex justify-between items-center">
                    <span>Password:</span>
                    <span className="text-text-primary font-mono bg-dark-200 px-2 py-1 rounded">{role.password}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-text-muted mt-2 text-center italic">{t(cta.noteKey)}</p>
      </div>
    </div>
  );
}
